import { LeaderboardPodium } from "@/src/components/leaderboard/leaderboard-podium";
import LeaderboardRow, {
  LeaderboardTableHeader,
} from "@/src/components/leaderboard/leaderboard-row";
import LeaderboardSkeleton from "@/src/components/leaderboard/leaderboard-skeleton";
import { LeaderboardUserPanel } from "@/src/components/leaderboard/leaderboard-user-panel";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import { useAuth } from "@/src/contexts/auth.context";
import { useGetRankedLeaderboardQuery } from "@/src/services/api/leaderboard/leaderboard.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { Cup } from "iconsax-react-nativejs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutChangeEvent,
  Modal,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

type LeaderboardTableProps = Readonly<{
  onBack: () => void;
}>;

const PANEL_WIDTH = 260;
const TABLE_MIN_WIDTH = 300;
const SIDE_BY_SIDE_THRESHOLD = 600;

const LeaderboardTable = ({ onBack }: LeaderboardTableProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const { user } = useAuth();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;
  const currentUserId = user?.itemId ?? "";

  const { data: rankedEntries = [], isLoading } =
    useGetRankedLeaderboardQuery();

  const currentUserEntry = useMemo(
    () => rankedEntries.find((e) => e.userId === currentUserId) ?? null,
    [rankedEntries, currentUserId],
  );

  // Starts null so the detail panel/modal only opens when a user is tapped
  // (initializing to the current user made the modal pop open on load).
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [containerWidth, setContainerWidth] = useState(0);

  const selectedEntry = useMemo(
    () => rankedEntries.find((e) => e.userId === selectedUserId) ?? null,
    [rankedEntries, selectedUserId],
  );

  const isPanelOpen = selectedEntry !== null;

  const top3 = useMemo(
    () => rankedEntries.filter((e) => e.rank <= 3),
    [rankedEntries],
  );

  const filteredEntries = useMemo(() => {
    const rest = rankedEntries.filter((e) => e.rank > 3);
    if (!search.trim()) return rest;
    return rest.filter((e) =>
      e.displayName.toLowerCase().includes(search.toLowerCase()),
    );
  }, [rankedEntries, search]);

  const handleRowPress = (userId: string) => {
    setSelectedUserId((prev) => (prev === userId ? null : userId));
  };

  const handleContainerLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  // Only computed after layout is measured (containerWidth > 0)
  const isSideBySide =
    containerWidth > 0 &&
    isPanelOpen &&
    containerWidth >= SIDE_BY_SIDE_THRESHOLD;

  const isModalMode =
    containerWidth > 0 &&
    isPanelOpen &&
    containerWidth < SIDE_BY_SIDE_THRESHOLD;

  const innerWidth = containerWidth > 0 ? containerWidth - 32 : TABLE_MIN_WIDTH;
  const tableWidth = isSideBySide
    ? Math.max(TABLE_MIN_WIDTH, innerWidth - PANEL_WIDTH - 12)
    : innerWidth;
  const panelWidth = isSideBySide ? PANEL_WIDTH : innerWidth;

  const searchAndBack = (
    <>
      <BackNavigationButton
        label={t("LABEL_BACK")}
        iconSize={18}
        iconClassName="text-slate"
        labelClassName="text-slate"
        onPress={onBack}
        containerStyle={{ marginBottom: 0 }}
      />
      <ScSearchInput
        value={search}
        onChangeText={setSearch}
        placeholderKey="LABEL_SEARCH_USERS"
        containerClassName="rounded-2xl flex-1 md:w-[50%]"
      />
    </>
  );

  const titleAndPodium = (
    <>
      <View className="flex-row items-center gap-2">
        <Cup
          size={22}
          color={themeVars["--color-primary-default"]}
          variant="Bold"
        />
        <Text className="text-sm font-semibold text-primary">
          {t("LABEL_LEADERBOARD")}
        </Text>
      </View>
      {!isLoading && (
        <LeaderboardPodium
          entries={top3}
          selectedUserId={selectedUserId}
          onPress={handleRowPress}
        />
      )}
    </>
  );

  const tableRows = (
    <>
      <LeaderboardTableHeader />
      {filteredEntries.map((entry, index) => (
        <LeaderboardRow
          key={`${entry.userId}-${index}`}
          entry={entry}
          isSelected={selectedUserId === entry.userId}
          isLast={index === filteredEntries.length - 1}
          onPress={() => handleRowPress(entry.userId)}
        />
      ))}
      {filteredEntries.length === 0 && (
        <View className="items-center py-8">
          <Text className="text-sm text-slate">{t("LABEL_NO_RESULTS")}</Text>
        </View>
      )}
    </>
  );

  const userModal = selectedEntry && (
    <Modal
      transparent
      animationType="fade"
      onRequestClose={() => setSelectedUserId(null)}
    >
      <Pressable
        style={varStyle}
        className="flex-1 bg-shadow-dark justify-center items-center px-6"
        onPress={() => setSelectedUserId(null)}
      >
        <Pressable onPress={(e) => e.stopPropagation()} className="w-full">
          <LeaderboardUserPanel
            entry={selectedEntry}
            isCurrentUser={selectedEntry.userId === currentUserId}
            onClose={() => setSelectedUserId(null)}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ── Mobile: independent cards (search · podium · table), no dividers ──
  if (isMobile) {
    return (
      <View className="gap-3">
        <View className="flex-row items-center gap-3 bg-chalk rounded-2xl p-3">
          {searchAndBack}
        </View>

        <View className="bg-chalk rounded-2xl p-4 gap-3">{titleAndPodium}</View>

        <View className="bg-chalk rounded-2xl overflow-hidden pb-2">
          {isLoading ? <LeaderboardSkeleton count={7} /> : tableRows}
        </View>

        {userModal}
      </View>
    );
  }

  // ── Web/desktop: original single-card layout with side-by-side panel ──
  return (
    <View className="gap-4">
      <View
        className="flex-1 rounded-2xl bg-chalk border border-ash-3"
        onLayout={handleContainerLayout}
      >
        <View className="flex-row p-3 items-center gap-5 border-b border-ash-3">
          {searchAndBack}
        </View>

        <View className="flex-1 p-4">
          <View className="flex-col items-start gap-2 mb-4">{titleAndPodium}</View>

          {isLoading && <LeaderboardSkeleton count={7} />}

          {!isLoading && (
            <View
              className={
                isSideBySide ? "flex-row gap-3 items-start" : "flex-col gap-3"
              }
            >
              <View
                className="rounded-2xl overflow-hidden bg-chalk border border-ash-3 pb-2"
                style={{ width: tableWidth }}
              >
                {tableRows}
              </View>

              {isSideBySide && selectedEntry && (
                <View style={{ width: panelWidth }}>
                  <LeaderboardUserPanel
                    entry={selectedEntry}
                    isCurrentUser={selectedEntry.userId === currentUserId}
                    onClose={() => setSelectedUserId(null)}
                  />
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      {isModalMode && userModal}
    </View>
  );
};

export default LeaderboardTable;
